import axios, { type AxiosProgressEvent, type AxiosRequestConfig } from 'axios';
import React, { useState, type Dispatch } from 'react';
import { xhr } from '@shared/config';
import { doAddFormDataToFormData } from '@shared/helpers/index.ts';
import { useErrorHandler } from '@shared/hooks/use-error-handler.ts';
import type { VideoPostRes, VideoType } from '@shared/types/common';

const axiosCreator = (setProgress: Dispatch<AxiosProgressEvent>, api: string) => {
  const instance = axios.create({
    withCredentials: false,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      setProgress(progressEvent);
    },
  });

  const postOnUploadUrl = async (uploadUrl: string, data: FormData) =>
    instance.post(uploadUrl, data);

  const post = async (data: FormData, config?: AxiosRequestConfig) =>
    xhr.post<VideoPostRes>(api, data, config);

  const patch = async (id: number, data: FormData) => xhr.patch(`${api}/${id}/`, data);

  const getCheck = async (id: number) => xhr.get<{ id: number }>(`${api}${id}/check/`);

  return {
    post,
    getCheck,
    patch,
    postOnUploadUrl,
  };
};

type VideoProps = {
  video_type: VideoType;

  url?: string;
  file?: File | string;
};

export const useVideoLoader = ({
  api,
  // additionalDataToPost,
  onError,
  onSuccess,
  onAction,
}: {
  api: string;
  // videoId?: number;
  // additionalDataToPost?: FormData;
  onSuccess?: (video: any) => void;
  onError?: (error: unknown) => void;
  onAction?: () => void;
}) => {
  const [progress, setProgress] = useState<AxiosProgressEvent>();
  const [uploadStatus, setUploadStatus] = React.useState<'idle' | 'loading'>('idle');
  const { handleError } = useErrorHandler();
  const { post, getCheck, postOnUploadUrl } = axiosCreator(setProgress, api);

  const create = async ({
    data,
    additionalDataToPost,
    config,
  }: {
    data: VideoProps;
    additionalDataToPost?: FormData;
    config?: AxiosRequestConfig;
  }) => {
    setUploadStatus('loading');

    try {
      onAction && onAction();
      const videoDataFromFront = new FormData();

      // video_type - обязателен в любом случае
      videoDataFromFront.append('video_type', data.video_type);
      // обработка url
      data.url && videoDataFromFront.append('embed_url', data.url);

      const res = await post(
        doAddFormDataToFormData(videoDataFromFront, additionalDataToPost),
        config
      );

      if (!res.data) throw res;

      const formData = new FormData();

      if (data.video_type === 'B') {
        data.file && formData.append('file', data.file);
        await postOnUploadUrl(res.data.upload_url, formData);
        const checkRes = await getCheck(res.data.id);
        if (!checkRes.data) throw checkRes;
      }

      setUploadStatus('idle');

      onSuccess && onSuccess(res.data);

      // если все прошло успешно - возвращаем айди созданного видео
      return res.data.id;
    } catch (error) {
      handleError(error);
      onError && onError(error);
      setUploadStatus('idle');
    }
  };

  return {
    create,
    progress,
    uploadStatus,
  };
};
